import { z } from 'zod'

import { DetailedApiError, DetailedApiErrorZ } from '@/types/error/ErrorResponse'

// eslint-disable-next-line no-restricted-syntax
export enum JobStatus {
	Sent = 'sent',
	Failed = 'failed',
	Completed = 'completed',
	InProgress = 'in progress',
}

export const JobStatusZ = z.enum(JobStatus)

const JobIdZ = z.string().brand<'JobId'>()
export type JobId = z.infer<typeof JobIdZ>
export interface WithJobId {
	job_id: JobId
}

export const CreateJobResponseZ = z.object({
	job_id: JobIdZ,
	status: JobStatusZ,
})
export type CreateJobResponse = z.infer<typeof CreateJobResponseZ>

// Separate type because inferring the `ReturnType` from `getJobResultResponseZ<T>` turned out to be problematic
export type JobResultResponse<T = unknown> =
	| {
			status: JobStatus.Sent
	  }
	| {
			status: JobStatus.InProgress
	  }
	| {
			status: JobStatus.Completed
			result: T
	  }
	| {
			status: JobStatus.Failed
			error: DetailedApiError
	  }

export type JobGroupResult<T = unknown> = (
	| {
			status: JobStatus.Completed
			result: T
	  }
	| {
			status: JobStatus.Failed
			error: DetailedApiError
	  }
)[]

export function getJobResultResponseZ<T extends z.ZodTypeAny = z.ZodUnknown>(resultSchema: T) {
	return z.discriminatedUnion('status', [
		z.strictObject({
			status: z.literal(JobStatus.Sent),
			result: z.null().optional(),
		}),
		z.strictObject({
			status: z.literal(JobStatus.InProgress),
			result: z.null().optional(),
		}),
		z.strictObject({
			status: z.literal(JobStatus.Completed),
			result: resultSchema,
		}),
		z.strictObject({
			status: z.literal(JobStatus.Failed),
			error: DetailedApiErrorZ,
		}),
	])
}

export function getJobGroupResultZ<T extends z.ZodTypeAny>(resultSchema: T) {
	return z.array(
		z.discriminatedUnion('status', [
			z.strictObject({
				status: z.literal(JobStatus.Completed),
				result: resultSchema,
			}),
			z.strictObject({
				status: z.literal(JobStatus.Failed),
				error: DetailedApiErrorZ,
			}),
		]),
	)
}
export function getJobGroupResultResponseZ<T extends z.ZodTypeAny>(resultSchema: T) {
	return getJobResultResponseZ(getJobGroupResultZ(resultSchema))
}
